import { AppShell, Button, Divider, Flex, Image, Menu, Text } from '@mantine/core';
import { NavLink, useLocation, useMatch, useNavigate } from 'react-router';
import { FullLogoIcon } from 'src/UI/icons';
import SquareLogo from 'src/UI/icons/logo/SquareLogo';
import classes from './HeaderRevamp.module.css';
import { useIsAfternoon } from 'src/common/useIsAfternoonHook';
import { Logger } from 'src/utils/Logger';
import { useLogout } from 'src/auth/hooks/useLogout';
import { showError } from 'src/notifications/notifications';
import { CLIENT_TABS_REGEX } from 'src/home/ClientData';
import { IconLogout, IconMoodEdit, IconSettings } from '@tabler/icons-react';
import { memo } from 'react';
import useCurrentUser from 'src/admin/users/hooks/useCurrentUser';
import { CreateButton } from 'src/common/components/CreateButton';
import { useFlags } from 'src/feature-flags/FlagsHook';
import PinnedFlagsPopover from 'src/feature-flags/PinnedFlagsPopover';

export const HEADER_HEIGHT = 72;

function NavigableLogo() {
  const navigate = useNavigate();

  return <LogoWithIcon onClick={() => navigate('/')} />;
}

export function LogoWithIcon({
  onClick,
  textColor = 'white',
}: { onClick?: () => void; textColor?: string } = {}) {
  return (
    <Flex
      align="center"
      h="100%"
      gap="xxs"
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : 'auto' }}
    >
      <SquareLogo />
      <FullLogoIcon height={34} color={textColor} />
    </Flex>
  );
}

export default function Header({
  showChat,
  isChatOpened,
  bg = 'navBackground',
  showSiestaButton = true,
}: {
  showChat: () => void;
  isChatOpened: boolean;
  bg?: string;
  showSiestaButton?: boolean;
}) {
  const navigate = useNavigate();
  const { isCrawford, isAfternoon } = useIsAfternoon();
  const { currentUser, isSystemAdmin } = useCurrentUser();
  const { mutate: logout } = useLogout({
    onError: error => {
      Logger.error('Log out failed', error);
      showError('Something went wrong while trying to log you out. Please try again.');
    },
  });
  const { flagEnabledFactFindSelfService } = useFlags();

  return (
    <AppShell.Header withBorder={false} className={classes.headerContainer} bg={bg}>
      <Flex align="center" h="100%" px="xxl" justify="space-between">
        <Flex h="100%" align="center" gap="44px">
          <NavigableLogo />
          <Flex align="center" h="100%" gap="sm">
            <MenuItem title="All clients" href="/clients/all" activeRegex={CLIENT_TABS_REGEX} />
            <MenuItem title="Company data" href="/company" activeRegex={/^\/company\/.*$/} />
            <MenuItem title="Meetings" href="/meetings" activeRegex={/^\/meetings\/.*$/} />
          </Flex>
        </Flex>

        <Flex align="center" h="100%" gap="xxl">
          <Flex align="center" h="100%" gap="sm">
            {isAfternoon && <PinnedFlagsPopover onClick={() => navigate('/secret-menu')} />}
            <CreateButton buttonProps={{ h: 40, variant: 'filled-pill', bg: 'brand.6' }} />
            {showSiestaButton && (
              <Button
                h={40}
                miw={165}
                justify="start"
                variant="filled-pill"
                bg={isChatOpened ? 'gray.3' : 'white'}
                onClick={showChat}
                leftSection={
                  <img
                    src="/images/sparkle-icon.png"
                    style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                    }}
                  />
                }
              >
                {isChatOpened ? 'Close Siesta AI' : 'Ask Siesta AI'}
              </Button>
            )}
          </Flex>

          <Menu
            transitionProps={{ transition: 'pop-top-right' }}
            position="bottom"
            width={220}
            withinPortal
          >
            <Menu.Target>
              <Image
                style={{
                  width: 40,
                  height: 40,
                }}
                radius="xl"
                src={
                  isCrawford
                    ? 'https://public-afternoon-bucket.s3.eu-north-1.amazonaws.com/crawford.jpeg'
                    : `https://ui-avatars.com/api/?background=0D8ABC&color=fff&name=${currentUser?.firstName} + ${currentUser?.lastName}`
                }
              />
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>Welcome, {currentUser?.firstName}!</Menu.Label>

              <Menu.Item
                leftSection={<IconMoodEdit size={16} />}
                onClick={() => {
                  navigate(`/user/details/${currentUser?.id}`);
                }}
              >
                My Details
              </Menu.Item>

              <Divider />

              {isSystemAdmin && (
                <>
                  <Menu.Label>Admin settings</Menu.Label>
                  <Menu.Item
                    leftSection={<IconSettings size={16} />}
                    onClick={() => navigate('/admin/users')}
                  >
                    User Management
                  </Menu.Item>
                  {flagEnabledFactFindSelfService && (
                    <Menu.Item
                      leftSection={<IconSettings size={16} />}
                      onClick={() => navigate('/admin/data-export-config')}
                    >
                      Data Export Configuration
                    </Menu.Item>
                  )}

                  <Divider />
                </>
              )}

              <Menu.Item leftSection={<IconLogout size={16} />} onClick={() => logout()}>
                Log out
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Flex>
      </Flex>
    </AppShell.Header>
  );
}

const MenuItem = memo(function MenuItem({
  title,
  href,
  activeRegex,
}: {
  title: string;
  href: string;
  activeRegex?: RegExp;
}) {
  const isActive = useIsActive(href, activeRegex);

  return (
    <NavLink to={href} className={classes.menuItem} data-active={isActive}>
      <Text size="md" fw={600} px="xxs" py="xxxs" style={{ textWrap: 'nowrap' }}>
        {title}
      </Text>
    </NavLink>
  );
});

function useIsActive(href: string, activeRegex?: RegExp) {
  const pathMatch = useMatch(href);
  const { pathname } = useLocation();

  if (activeRegex != null && activeRegex.test(pathname)) {
    return true;
  }

  return pathMatch != null;
}
